import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { avatarService } from '../../services/api';

// État initial
const initialState = {
  avatars: [],
  currentAvatar: null,
  loading: false,
  error: null,
  total: 0
};

// Actions asynchrones
export const fetchAvatars = createAsyncThunk(
  'avatars/fetchAvatars',
  async (_, { rejectWithValue }) => {
    try {
      const response = await avatarService.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAvatarById = createAsyncThunk(
  'avatars/fetchAvatarById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await avatarService.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createAvatar = createAsyncThunk(
  'avatars/createAvatar',
  async (avatarData, { rejectWithValue }) => {
    try {
      const response = await avatarService.create(avatarData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAvatar = createAsyncThunk(
  'avatars/updateAvatar',
  async ({ id, avatarData }, { rejectWithValue }) => {
    try {
      const response = await avatarService.update(id, avatarData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAvatar = createAsyncThunk(
  'avatars/deleteAvatar',
  async (id, { rejectWithValue }) => {
    try {
      await avatarService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const avatarSlice = createSlice({
  name: 'avatar',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentAvatar: (state, action) => {
      state.currentAvatar = action.payload;
    },
    clearCurrentAvatar: (state) => {
      state.currentAvatar = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all avatars
      .addCase(fetchAvatars.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvatars.fulfilled, (state, action) => {
        state.loading = false;
        state.avatars = action.payload.data || action.payload;
        state.total = action.payload.total || state.avatars.length;
        state.error = null;
      })
      .addCase(fetchAvatars.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch avatar by ID
      .addCase(fetchAvatarById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvatarById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentAvatar = action.payload;
        state.error = null;
      })
      .addCase(fetchAvatarById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create avatar
      .addCase(createAvatar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAvatar.fulfilled, (state, action) => {
        state.loading = false;
        state.avatars.push(action.payload);
        state.error = null;
      })
      .addCase(createAvatar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update avatar
      .addCase(updateAvatar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAvatar.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.avatars.findIndex(avatar => avatar.id === action.payload.id);
        if (index !== -1) {
          state.avatars[index] = action.payload;
        }
        if (state.currentAvatar && state.currentAvatar.id === action.payload.id) {
          state.currentAvatar = action.payload;
        }
        state.error = null;
      })
      .addCase(updateAvatar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete avatar
      .addCase(deleteAvatar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAvatar.fulfilled, (state, action) => {
        state.loading = false;
        state.avatars = state.avatars.filter(avatar => avatar.id !== action.payload);
        if (state.currentAvatar && state.currentAvatar.id === action.payload) {
          state.currentAvatar = null;
        }
        state.error = null;
      })
      .addCase(deleteAvatar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setCurrentAvatar, clearCurrentAvatar } = avatarSlice.actions;

export default avatarSlice.reducer;
